import { FunctionComponent, useState } from 'react';
import {
    Box,
    Drawer,
    Stack,
    SvgIcon,
    Typography,
    useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { StarjackTable } from '@/components/Table/v2/Table';
import { usePagination } from '@/hooks/usePagination';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@heroicons/react/24/solid/TrashIcon';
import { useGetAgenciesQuery } from '@/api/v2/agency';
import {
    Agency,
    AgencyWithCredentials,
    createEmptyAgencyWithCredentials,
} from '@/models/agency';
import { formatStarjackDate } from '@/utils/time';
import { AgencyDrawerContent } from '@/pageContents/agencies/AgencyDrawerContent';
import { AgencyDeleteDialog } from '@/pageContents/agencies/AgencyDeleteDialog';

export const AgenciesContent: FunctionComponent = () => {
    const theme = useTheme();
    const { data, isLoading: hospitalityUsersLoading } = useGetAgenciesQuery();
    const { currentItems, page, setNextPage, setItemsPerPage, itemsPerPage } =
        usePagination({
            items: data,
            itemsPerPage: 10,
        });
    const [agencyToEdit, setAgencyToEdit] = useState<{
        isOpen: boolean;
        agency: AgencyWithCredentials;
    }>({
        isOpen: false,
        agency: null,
    });
    const [agencyToDelete, setAgencyToDelete] = useState<{
        toDelete: Agency;
        isOpen: boolean;
    }>({ toDelete: null, isOpen: false });

    const closeDrawer = () => {
        setAgencyToEdit((prevState) => ({
            ...prevState,
            isOpen: false,
        }));
    };

    return (
        <>
            <Typography
                sx={{
                    mb: 8,
                }}
                variant='h4'
            >
                Agenturen
            </Typography>
            <Stack spacing={theme.page.verticalSpacing}>
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        width: '100%',
                        p: 3,
                    }}
                >
                    <LoadingButton
                        onClick={() => {
                            setAgencyToEdit({
                                isOpen: true,
                                agency: createEmptyAgencyWithCredentials(),
                            });
                        }}
                        variant='contained'
                        loading={false}
                    >
                        + Neue Agentur anlegen
                    </LoadingButton>
                </Box>
                <StarjackTable<Agency>
                    count={data?.length}
                    items={currentItems}
                    columns={[
                        {
                            label: 'Benutzername',
                            itemKey: 'username',
                        },
                        {
                            label: 'Liftoperator Id ',
                            itemKey: 'liftOperatorId',
                        },

                        {
                            label: 'Erstellt am',
                            itemTransform: (item) =>
                                formatStarjackDate(item.createdAt),
                        },

                        {
                            label: 'Aktion',
                            itemTransform: (item) => (
                                <IconButton
                                    onClick={() => {
                                        setAgencyToDelete({
                                            isOpen: true,
                                            toDelete: item,
                                        });
                                    }}
                                >
                                    <SvgIcon fontSize='small'>
                                        <DeleteIcon />
                                    </SvgIcon>
                                </IconButton>
                            ),
                        },
                    ]}
                    currentPage={page}
                    itemId={'id'}
                    loading={hospitalityUsersLoading}
                    onPageChange={setNextPage}
                    rowsPerPage={itemsPerPage}
                    onRowsPerPageChange={setItemsPerPage}
                />

                <Drawer
                    anchor='right'
                    open={agencyToEdit.isOpen}
                    onClose={closeDrawer}
                >
                    <AgencyDrawerContent
                        agency={agencyToEdit.agency}
                        onClose={closeDrawer}
                    />
                </Drawer>

                <AgencyDeleteDialog
                    open={agencyToDelete.isOpen}
                    onClose={() => {
                        setAgencyToDelete((prevState) => ({
                            ...prevState,
                            isOpen: false,
                        }));
                    }}
                    agency={agencyToDelete.toDelete}
                />
            </Stack>
        </>
    );
};
